'use client';

import { useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from 'react-use';

import { readBootThreadId } from '@/lib/sync/store';

export const BOOT_RESTORE_WELCOME_TIMEOUT_MS = 3000;

/**
 * Suppresses the empty-thread welcome screen while a resumable tenant restores onto a persisted
 * boot thread — either a fresh load or the cross-tab same-thread reload. In both, the runtime
 * mounts a blank thread for a few hundred ms before `initialThreadId` loads its messages, which
 * would otherwise flash the welcome (with suggestion chips) as if the conversation had vanished.
 *
 * Armed before paint (layout effect + client-only `readBootThreadId` read → no welcome frame, no
 * SSR/hydration mismatch) and one-shot (`armedRef`): once the first restore settles it never
 * re-arms, so a later empty "new chat" shows its welcome normally. Returns `false` unbatched for
 * non-resumable tenants (`enabled` false) so their render path stays byte-identical.
 */
export function useBootRestoreWelcomeGate({
  enabled,
  messageCount,
  isRunning,
}: {
  enabled: boolean;
  messageCount: number;
  isRunning: boolean;
}): boolean {
  const [restoring, setRestoring] = useState(false);
  const armedRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    if (enabled && !armedRef.current && readBootThreadId() != null) {
      armedRef.current = true;
      setRestoring(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (!restoring) return;
    if (messageCount > 0 || isRunning) {
      setRestoring(false);
      return;
    }
    const timer = setTimeout(() => setRestoring(false), BOOT_RESTORE_WELCOME_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [restoring, messageCount, isRunning]);

  return restoring;
}
